import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import store, { persistor } from './redux/store'; // This matches your store file exports
import { AuthProvider } from './contexts/AuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers'; // Import LocalizationProvider
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'; // Use Moment.js adapter
import Layout from './components/Layout';
import Dashboard from './pages/crm/dashboard/Dashboard';
import LeadsList from './pages/crm/leads/LeadsList';
import LeadDetails from './pages/crm/leads/LeadDetails';
import Accounts from './pages/crm/accounts/Accounts';
import AccountSettings from './pages/crm/accounts/AccountSettings';
import Funnels from './pages/crm/funnels/Funnels';
import FunnelSettings from './pages/crm/funnels/FunnelSettings';
import Campaigns from './pages/crm/campaigns/Campaigns';
import CampaignSettings from './pages/crm/campaigns/CampaignSettings';
import LeadNurturingCampaignSelection from './pages/nurturing/campaigns/base/LeadNurturingCampaignSelection';
import LeadNurturingCampaigns from './pages/nurturing/campaigns/base/LeadNurturingCampaigns';
import Profile from './pages/settings/Profile';
import Login from './pages/login/Login';
import Signup from './pages/login/Signup';
import ToDoPage from './pages/crm/tasks/ToDoPage';
import IntegrationsPage from './pages/crm/integrations/IntegrationsPage';
import ProtectedRoute from './components/ProtectedRoute';
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials } from './redux/authSlice';
import ForgotPassword from './pages/login/ForgotPassword';
import ResetPassword from './pages/login/ResetPassword';
import ActivateAccount from './pages/login/ActivateAccount'; 
import SetPassword from './pages/settings/SetPassword'; 
import NotificationsPage from './pages/crm/notifications/NotificationsPage';
import NotificationRulesPage from './pages/crm/notifications/NotificationRulesPage';
import useNotificationPolling from './hooks/useNotificationPolling';
import CustomSnackbar from './components/CustomSnackbar';
import AgentDashboard from './components/AgentDashboard';
import CreateAutomationsPage from './pages/crm/automations/CreateAutomationsPage';
import CreateWorkflowPage from './pages/crm/automations/CreateWorkflowPage';
import WorkflowOverviewPage from './pages/crm/automations/WorkflowOverviewPage';
import WorkflowAutomationsPage from './pages/crm/automations/WorkflowAutomationsPage';
import CustomViews from './pages/crm/custom_views/CustomViews';
import CustomViewBuilder from './pages/crm/custom_views/CustomViewBuilder';
import CustomViewEdit from './pages/crm/custom_views/CustomViewEdit';
import CustomViewRenderPage from './pages/crm/custom_views/CustomViewRenderPage';
import ConversationsInbox from './pages/nurturing/conversations/ConversationsInbox';
import ConversationsTemplates from './pages/nurturing/conversations/ConversationsTemplates';
import ConversationsScheduledMessages from './pages/nurturing/conversations/ConversationsScheduledMessages';
import NotificationSettings from './pages/crm/notifications/NotificationSettings';
import JourneysListPage from './pages/nurturing/journeys/JourneysListPage';
import CreateJourneyPage from './pages/nurturing/journeys/CreateJourneyPage';
import JourneyBuilderPage from './pages/nurturing/journeys/JourneyBuilderPage';
import CreateJourneyBasedCampaign from './pages/nurturing/campaigns/journey/CreateJourneyBasedCampaign';
import CreateDripCampaign from './pages/nurturing/campaigns/drip/CreateDripCampaign';
import CreateReminderCampaign from './pages/nurturing/campaigns/reminder/CreateReminderCampaign';
import CreateBlastCampaign from './pages/nurturing/campaigns/blast/CreateBlastCampaign';
import EditDripCampaign from './pages/nurturing/campaigns/drip/EditDripCampaign';
import EditReminderCampaign from './pages/nurturing/campaigns/reminder/EditReminderCampaign';
import EditBlastCampaign from './pages/nurturing/campaigns/blast/EditBlastCampaign';
import EditJourneyBasedCampaign from './pages/nurturing/campaigns/journey/EditJourneyBasedCampaign';
import LeadSelectionPage from './pages/nurturing/lead_selection/LeadSelectionPage';
import LeadLists from './pages/crm/lists/LeadLists';
import CreateLeadList from './pages/crm/lists/CreateLeadList';
import EditLeadList from './pages/crm/lists/EditLeadList';
import LeadListDetail from './pages/crm/lists/LeadListDetail';
import AddLeadsToList from './pages/crm/lists/AddLeadsToList';

// Lead Generation imports
import CampaignsList from './pages/lead_gen/campaigns/CampaignsList';
import CreateCampaign from './pages/lead_gen/campaigns/CreateCampaign';
import LeadSourcesList from './pages/lead_gen/sources/LeadSourcesList';
import SuppressionLists from './pages/lead_gen/suppression/SuppressionLists';
import CreateSuppressionList from './pages/lead_gen/suppression/CreateSuppressionList';
import SuppressionListDetail from './pages/lead_gen/suppression/SuppressionListDetail';
import EditSuppressionList from './pages/lead_gen/suppression/EditSuppressionList';
import BulkUploadSuppression from './pages/lead_gen/suppression/BulkUploadSuppression';
import BulkImportJobs from './pages/lead_gen/suppression/BulkImportJobs';
import JobsAndResults from './pages/lead_gen/jobs/JobsAndResults';
import LeadGenSettings from './pages/lead_gen/settings/LeadGenSettings';

const theme = createTheme();

const notificationSound = new Audio('/sounds/notification-sound.wav');

function AppContent() {
  const dispatch = useDispatch();
  const notifications = useSelector(state => state.notifications.items);
  const soundEnabled = useSelector(state => state.notifications.soundEnabled);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');
    if (token && refreshToken) {
      dispatch(setCredentials({ token, refreshToken }));
    }
  }, [dispatch]);

  // Call the hook at the top level, it will handle its own authentication check
  useNotificationPolling();

  useEffect(() => {
    if (notifications.length > 0 && soundEnabled) {
      notificationSound.play().catch(error => {
        console.log('Error playing notification sound:', error);
      });
    }
  }, [notifications, soundEnabled]);

  return (
    <Router>
      <Layout>
        <CustomSnackbar />
        <Routes>
          {/* Auth Routes */}
          <Route path="/activate/:uid/:token" element={<ActivateAccount />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          
          {/* CRM Routes (Protected Routes) */}
          <Route path="/crm/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/crm/leads" element={<ProtectedRoute><LeadsList /></ProtectedRoute>} /> 
          <Route path="/crm/leads/:leadId/details" element={<ProtectedRoute><LeadDetails /></ProtectedRoute>} />
          <Route path="/crm/lists" element={<ProtectedRoute><LeadLists /></ProtectedRoute>} />
          <Route path="/crm/lists/create" element={<ProtectedRoute><CreateLeadList /></ProtectedRoute>} />
          <Route path="/crm/lists/:listId" element={<ProtectedRoute><LeadListDetail /></ProtectedRoute>} />
          <Route path="/crm/lists/:listId/edit" element={<ProtectedRoute><EditLeadList /></ProtectedRoute>} />
          <Route path="/crm/lists/:listId/add-leads" element={<ProtectedRoute><AddLeadsToList /></ProtectedRoute>} />
          <Route path="/crm/funnels" element={<ProtectedRoute><Funnels /></ProtectedRoute>} />
          <Route path="/crm/funnel/:funnelId" element={<ProtectedRoute><FunnelSettings /></ProtectedRoute>} /> 
          <Route path="/crm/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
          <Route path="/crm/campaign/:campaignId" element={<ProtectedRoute><CampaignSettings /></ProtectedRoute>} />         
          <Route path="/crm/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
          <Route path="/crm/account/:accountId" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
          <Route path="/crm/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/crm/tasks" element={<ProtectedRoute><ToDoPage /></ProtectedRoute>} />
          <Route path="/crm/custom-views" element={<ProtectedRoute><CustomViews /></ProtectedRoute>} />
          <Route path="/crm/custom-views/create" element={<ProtectedRoute><CustomViewBuilder /></ProtectedRoute>} />
          <Route path="/crm/custom-views/:id/edit" element={<ProtectedRoute><CustomViewEdit /></ProtectedRoute>} />
          <Route path="/crm/custom-views/:id" element={<ProtectedRoute><CustomViewRenderPage /></ProtectedRoute>} />
          <Route path="/crm/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="/crm/notification-rules" element={<ProtectedRoute><NotificationRulesPage /></ProtectedRoute>} />
          <Route path="/crm/conversations/inbox" element={<ProtectedRoute><ConversationsInbox /></ProtectedRoute>} />
          <Route path="/crm/conversations/templates" element={<ProtectedRoute><ConversationsTemplates /></ProtectedRoute>} />
          <Route path="/crm/notification-settings" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          {/* Integrations & Automations Routes */}
          <Route
            path="/crm/integrations"
            element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/automations"
            element={
              <ProtectedRoute>
                <CreateAutomationsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/crm/agent-dashboard" element={<ProtectedRoute><AgentDashboard /></ProtectedRoute>} />
          <Route
            path="/crm/automations/create/:platformId"
            element={
              <ProtectedRoute>
                <CreateWorkflowPage />
              </ProtectedRoute>
            }
          />

          {/* Workflow routes */}
          <Route path="/crm/automations/workflows/create" element={<CreateWorkflowPage />} />
          <Route path="/crm/automations/workflows/:workflowId" element={<WorkflowOverviewPage />} />
          <Route path="/crm/automations/workflows/view" element={<WorkflowAutomationsPage />} />
          <Route path="/crm/automations" element={<CreateAutomationsPage />} />

          {/* Leaed Nurturing routes */}
          <Route path="/crm/lead-nurturing" element={<ProtectedRoute><LeadNurturingCampaigns /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/create" element={<ProtectedRoute><LeadNurturingCampaignSelection /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/create/drip" element={<ProtectedRoute><CreateDripCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/create/reminder" element={<ProtectedRoute><CreateReminderCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/create/blast" element={<ProtectedRoute><CreateBlastCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/create/journey" element={<ProtectedRoute><CreateJourneyBasedCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/edit/drip/:campaignId" element={<ProtectedRoute><EditDripCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/edit/reminder/:campaignId" element={<ProtectedRoute><EditReminderCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/edit/blast/:campaignId" element={<ProtectedRoute><EditBlastCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/edit/journey/:campaignId" element={<ProtectedRoute><EditJourneyBasedCampaign /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/:campaignId/lead-selection" element={<ProtectedRoute><LeadSelectionPage /></ProtectedRoute>} />
          <Route path="/crm/lead-nurturing/scheduled-messages" element={<ProtectedRoute><ConversationsScheduledMessages /></ProtectedRoute>} />
          
          {/* Journeys routes */}
          <Route path="/crm/journeys" element={<ProtectedRoute><JourneysListPage /></ProtectedRoute>} />
          <Route path="/crm/journeys/create" element={<ProtectedRoute><CreateJourneyPage /></ProtectedRoute>} />
          <Route path="/crm/journeys/:journeyId/builder" element={<ProtectedRoute><JourneyBuilderPage /></ProtectedRoute>} />

          {/* Lead Generation Routes */}
          <Route path="/lead-gen" element={<ProtectedRoute><CampaignsList /></ProtectedRoute>} />
          <Route path="/lead-gen/campaigns" element={<ProtectedRoute><CampaignsList /></ProtectedRoute>} />
          <Route path="/lead-gen/campaigns/create" element={<ProtectedRoute><CreateCampaign /></ProtectedRoute>} />
          <Route path="/lead-gen/sources" element={<ProtectedRoute><LeadSourcesList /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression" element={<ProtectedRoute><SuppressionLists /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression/create" element={<ProtectedRoute><CreateSuppressionList /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression/bulk-upload" element={<ProtectedRoute><BulkUploadSuppression /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression/bulk-jobs" element={<ProtectedRoute><BulkImportJobs /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression/:id/edit" element={<ProtectedRoute><EditSuppressionList /></ProtectedRoute>} />
          <Route path="/lead-gen/suppression/:id" element={<ProtectedRoute><SuppressionListDetail /></ProtectedRoute>} />
          <Route path="/lead-gen/jobs" element={<ProtectedRoute><JobsAndResults /></ProtectedRoute>} />
          <Route path="/lead-gen/settings" element={<ProtectedRoute><LeadGenSettings /></ProtectedRoute>} />

          {/* Default route (usually a 404 page or redirect to home) */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Layout>
    </Router>
  );
}

function App() {
  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DndProvider backend={HTML5Backend}>
                <AppContent />
              </DndProvider>
            </LocalizationProvider>
          </ThemeProvider>
        </AuthProvider>
      </PersistGate>
    </ReduxProvider>
  );
}

export default App;